import { DeleteOutlined, UserOutlined } from '@ant-design/icons'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Avatar, Button, Collapse, Flex, List, Popconfirm } from 'antd'
import { useTheme } from 'antd-style'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceDeleteGuardianshipEndpoint,
  usePersonsServiceGetGuardianshipListEndpointKey,
  usePersonsServiceGetPersonByIdnpEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetGuardianshipListResponse,
  PersonsService
} from 'src/api/requests'
import { CollapseExpandArrow, Text } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from '../styles'
import { TutorDetails } from '../TutorDetails'

type Props = {
  data?: GetGuardianshipListResponse['guardianships']
}
export const TutorForm = ({ data }: Props) => {
  const { t } = useLocales()
  const { id } = useParams()
  const { styles } = useStyles()
  const palette = useTheme()
  const queryClient = useQueryClient()

  const { mutate: deleteTutor } = usePersonsServiceDeleteGuardianshipEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetGuardianshipListEndpointKey, String(id)]
      })
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onDelete = (id?: string) => {
    deleteTutor({
      id: String(id)
    })
  }

  const personDetailsQueries = useQueries({
    queries:
      data?.map((item) => ({
        queryKey: [usePersonsServiceGetPersonByIdnpEndpointKey, item?.idnp],
        queryFn: async () => {
          const response = await PersonsService.getPersonByIdnpEndpoint(
            String(item?.idnp)
          )

          return response
        },
        enabled: !!data
      })) || []
  })

  return (
    <Flex vertical gap={8} align="start">
      <Text> {t('persons.assigned_persons')}</Text>

      <List
        itemLayout="horizontal"
        dataSource={data}
        className="full-width"
        renderItem={(item) => {
          const personQuery = personDetailsQueries.find(
            (person) => person?.data?.idnp === item?.idnp
          )

          const { data: personDetails } = personQuery || {}

          return (
            <Collapse
              key={item?.personId}
              expandIconPosition="end"
              className={styles.collapseItem}
              expandIcon={({ isActive }) => (
                <Flex gap={16}>
                  <CollapseExpandArrow isActive={isActive} />

                  <Popconfirm
                    title={t('persons.delete_tutor')}
                    onConfirm={(e) => {
                      e?.stopPropagation()
                      onDelete(String(item?.guardianshipId))
                    }}
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </Flex>
              )}
              items={[
                {
                  label: (
                    <Flex gap={16} align="center">
                      <Flex>
                        <Avatar icon={<UserOutlined />} />
                      </Flex>
                      <Flex vertical>
                        <Flex gap={4}>
                          <Text strong>
                            {item?.firstName} {item?.lastName}
                          </Text>
                        </Flex>
                        <Text color={palette.grey.grey1}>ID: {item?.idnp}</Text>
                      </Flex>
                    </Flex>
                  ),
                  children: (
                    <Flex vertical className="full-width" gap={8}>
                      <TutorDetails
                        data={personDetails}
                        guardianshipId={item?.guardianshipId}
                      />
                    </Flex>
                  ),
                  key: item?.personId
                }
              ]}
            />
          )
        }}
      />
    </Flex>
  )
}
